import { FieldItem, ModifiedLogValue } from '@lp/features/RoutePlanner/components/LogisticsChangeLog/utils';
import {
  convertAircraftResourcesToValue,
  convertCrewMemberToValue,
  convertDateTimeChangedValues,
  convertDriverChangesToValue,
  convertHotelDateTimeChangedValues,
  convertHotelLocationChangedValues,
  convertLocationChangedValues,
  convertPriceChangesToValue,
  convertPrimitiveToString,
  convertProviderChangesToValue,
  convertReassignedTripReference,
  convertResourcesToValue,
} from '@lp/features/RoutePlanner/components/LogisticsChangeLog/utils/converters';
import { LogisticsChangeLog, LogisticsChangeLogType, RouteTypeEnum, SegmentTypeEnum } from '@/api/apis/route';

export type ChangeLogItemProps = {
  log: LogisticsChangeLog;
};

export const ADD_REMOVE_ROUTE_LEG_LOGS = [
  LogisticsChangeLogType.TmdxJetAviaLegAddedLog,
  LogisticsChangeLogType.SupplementalLiftAviaLegAddedLog,
  LogisticsChangeLogType.TxCenterProviderAviaLegAddedLog,
  LogisticsChangeLogType.CommercialFlightAviaLegAddedLog,
  LogisticsChangeLogType.TmdxAffiliateGroundLegAddedLog,
  LogisticsChangeLogType.TxCenterProviderGroundLegAddedLog,
  LogisticsChangeLogType.CurriServiceGroundLegAddedLog,
  LogisticsChangeLogType.TmdxJetAviaLegRemovedLog,
  LogisticsChangeLogType.SupplementalLiftAviaLegRemovedLog,
  LogisticsChangeLogType.TxCenterProviderAviaLegRemovedLog,
  LogisticsChangeLogType.CommercialFlightAviaLegRemovedLog,
  LogisticsChangeLogType.TmdxAffiliateGroundLegRemovedLog,
  LogisticsChangeLogType.TxCenterProviderGroundLegRemovedLog,
  LogisticsChangeLogType.CurriServiceGroundLegRemovedLog,
  LogisticsChangeLogType.TmdxDriveAffiliateGroundLegAddedLog,
  LogisticsChangeLogType.TmdxDriveAffiliateGroundLegRemovedLog,
  LogisticsChangeLogType.TmdxDriveTedsGroundLegAddedLog,
  LogisticsChangeLogType.TmdxDriveTedsGroundLegRemovedLog,
  LogisticsChangeLogType.SelfServicedGroundLegAddedLog,
  LogisticsChangeLogType.SelfServicedGroundLegRemovedLog,
];
export const MODIFIED_AVIA_ROUTE_LEG_LOGS = [
  LogisticsChangeLogType.TmdxJetAviaLegModifiedLog,
  LogisticsChangeLogType.SupplementalLiftAviaLegModifiedLog,
  LogisticsChangeLogType.TxCenterProviderAviaLegModifiedLog,
  LogisticsChangeLogType.CommercialFlightAviaLegModifiedLog,
  LogisticsChangeLogType.SabreCommercialAviaLegModifiedLog,
];
export const MODIFIED_GROUND_ROUTE_LEG_LOGS = [
  LogisticsChangeLogType.TmdxAffiliateGroundLegModifiedLog,
  LogisticsChangeLogType.TxCenterProviderGroundLegModifiedLog,
  LogisticsChangeLogType.CurriServiceGroundLegModifiedLog,
  LogisticsChangeLogType.TmdxDriveAffiliateGroundLegModifiedLog,
  LogisticsChangeLogType.TmdxDriveTedsGroundLegModifiedLog,
  LogisticsChangeLogType.SelfServicedGroundLegModifiedLog,
];
export const ADD_REMOVE_ACCOMMODATION_LEG_LOGS = [
  LogisticsChangeLogType.AccommodationLegAddedLog,
  LogisticsChangeLogType.AccommodationLegRemovedLog,
];
export const ACCOMMODATION_LEG_LOGS = [
  ...ADD_REMOVE_ACCOMMODATION_LEG_LOGS,
  LogisticsChangeLogType.AccommodationLegModifiedLog,
];
export const ADD_REMOVE_SABRE_COMMERCIAL_LEG_LOGS = [
  LogisticsChangeLogType.SabreCommercialAviaLegsAddedLog,
  LogisticsChangeLogType.SabreCommercialAviaGroupRemovedLog,
];
export const HUB_LOGS = [
  LogisticsChangeLogType.RouteDepartureHubAssignedLog,
  LogisticsChangeLogType.RouteDepartureHubUnassignedLog,
  LogisticsChangeLogType.RouteArrivalHubAssignedLog,
  LogisticsChangeLogType.RouteArrivalHubUnassignedLog,
];
export const SUGGESTED_ROUTES_APPLIED_LOGS = [
  LogisticsChangeLogType.SuggestedRoutesAppliedLog,
  LogisticsChangeLogType.PartialSuggestedRoutesAppliedLog,
];
export const GROUND_TRIP_ASSIGNED_LOGS = [
  LogisticsChangeLogType.GroundTripToAffiliateLegAssignedLog,
  LogisticsChangeLogType.GroundTripToTedsLegAssignedLog,
];
export const GROUND_TRIP_REASSIGNED_LOGS = [
  LogisticsChangeLogType.GroundTripToAffiliateLegReassignedLog,
  LogisticsChangeLogType.GroundTripToTedsLegReassignedLog,
];

export const ROUTE_TYPE_NAME: Record<RouteTypeEnum, string> = {
  [RouteTypeEnum.Main]: 'Main route',
  [RouteTypeEnum.DryRun]: 'Dry Run',
};

export const SEGMENT_TYPE_NAME: Record<SegmentTypeEnum, string> = {
  [SegmentTypeEnum.hospital]: 'Hub - DH',
  [SegmentTypeEnum.txcenter]: 'DH - TXC',
  [SegmentTypeEnum.hub]: 'TXC - Hub',
  [SegmentTypeEnum.dhToHub]: 'DH - Hub',
  [SegmentTypeEnum.custom]: 'Custom',
  [SegmentTypeEnum.reposition]: 'Repositioning',
  [SegmentTypeEnum.returnToBase]: 'Return to base',
};

export const SCS_MODIFIED_LOG_VALUE: ModifiedLogValue = {
  type: 'scs',
  title: 'Static Cold Storage',
};

// Modified values
const COMMENTS_MODIFIED_FIELD: FieldItem = {
  fieldName: 'Comment',
  fields: ['comments'],
  converter: convertPrimitiveToString,
};

const DEFAULT_MODIFIED_FIELDS_LIST: FieldItem[] = [
  {
    fieldName: 'Departure',
    fields: ['departureLocation'],
    converter: convertLocationChangedValues('departureLocation'),
  },
  {
    fieldName: 'Arrival',
    fields: ['arrivalLocation'],
    converter: convertLocationChangedValues('arrivalLocation'),
  },
  {
    fieldName: 'Departure time',
    fields: ['departureTime', 'departureLocalTimezone'],
    converter: convertDateTimeChangedValues('departureTime', 'departureLocalTimezone'),
  },
  {
    fieldName: 'Arrival time',
    fields: ['arrivalTime', 'arrivalLocalTimezone'],
    converter: convertDateTimeChangedValues('arrivalTime', 'arrivalLocalTimezone'),
  },
  {
    fieldName: 'Resources',
    fields: ['resources', 'hasScs'],
    converter: convertResourcesToValue,
  },
];

const AVIA_PROVIDER_MODIFIED_FIELDS: FieldItem[] = [
  {
    fieldName: "Aircraft's tail number",
    fields: ['tailNumber'],
    converter: convertPrimitiveToString,
  },
  {
    fieldName: 'First crew member',
    fields: ['firstCrewMember'],
    converter: convertCrewMemberToValue('firstCrewMember'),
  },
  {
    fieldName: 'Second crew member',
    fields: ['secondCrewMember'],
    converter: convertCrewMemberToValue('secondCrewMember'),
  },
];

// Avia legs
export const TMDX_JET_AVIA_MODIFIED_FIELDS_LIST: FieldItem[] = [
  ...DEFAULT_MODIFIED_FIELDS_LIST,
  {
    fieldName: "Aircraft's tail number",
    fields: ['resources'],
    converter: convertAircraftResourcesToValue,
  },
  COMMENTS_MODIFIED_FIELD,
];

export const SUPPLEMENTAL_LIFT_AVIA_MODIFIED_FIELDS_LIST: FieldItem[] = [
  ...DEFAULT_MODIFIED_FIELDS_LIST,
  {
    fieldName: 'Supplemental Lift Provider',
    fields: ['provider'],
    converter: convertProviderChangesToValue,
  },
  ...AVIA_PROVIDER_MODIFIED_FIELDS,
  {
    fieldName: 'Price',
    fields: ['priceInCents'],
    converter: convertPriceChangesToValue,
  },
  COMMENTS_MODIFIED_FIELD,
];

export const TX_CENTER_AVIA_MODIFIED_FIELDS_LIST: FieldItem[] = [
  ...DEFAULT_MODIFIED_FIELDS_LIST,
  {
    fieldName: 'TX Center Provider',
    fields: ['provider'],
    converter: convertProviderChangesToValue,
  },
  ...AVIA_PROVIDER_MODIFIED_FIELDS,
  COMMENTS_MODIFIED_FIELD,
];

export const COMMERCIAL_AVIA_MODIFIED_FIELDS_LIST: FieldItem[] = [
  ...DEFAULT_MODIFIED_FIELDS_LIST,
  {
    fieldName: 'Flight number',
    fields: ['flightNumber'],
    converter: convertPrimitiveToString,
  },
  {
    fieldName: 'Price',
    fields: ['priceInCents'],
    converter: convertPriceChangesToValue,
  },
  {
    fieldName: 'Booking number',
    fields: ['bookingNumber'],
    converter: convertPrimitiveToString,
  },
  COMMENTS_MODIFIED_FIELD,
];

// Ground legs
export const NON_TMDX_GROUND_MODIFIED_FIELDS_LIST: FieldItem[] = [
  {
    fieldName: 'Driver',
    fields: ['driver', 'phoneNumber'],
    converter: convertDriverChangesToValue,
  },
  {
    fieldName: "Driver's email",
    fields: ['email'],
    converter: convertPrimitiveToString,
  },
  {
    fieldName: 'Plate numbers',
    fields: ['plateNumber'],
    converter: convertPrimitiveToString,
  },
  {
    fieldName: 'Vehicle type',
    fields: ['vehicleType'],
    converter: convertPrimitiveToString,
  },
  {
    fieldName: 'Reservation number',
    fields: ['reservationNumber'],
    converter: convertPrimitiveToString,
  },
  {
    fieldName: 'Pickup',
    fields: ['pickup'],
    converter: convertPrimitiveToString,
  },
  {
    fieldName: 'Dropoff',
    fields: ['dropoff'],
    converter: convertPrimitiveToString,
  },
  {
    fieldName: 'Wait',
    fields: ['wait'],
    converter: convertPrimitiveToString,
  },
  {
    fieldName: 'Stay',
    fields: ['stay'],
    converter: convertPrimitiveToString,
  },
];

export const TMDX_AFFILIATE_GROUND_MODIFIED_FIELDS_LIST: FieldItem[] = [
  ...DEFAULT_MODIFIED_FIELDS_LIST,
  ...NON_TMDX_GROUND_MODIFIED_FIELDS_LIST,
  COMMENTS_MODIFIED_FIELD,
];

export const TMDX_DRIVE_GROUND_MODIFIED_FIELDS_LIST: FieldItem[] = [
  ...DEFAULT_MODIFIED_FIELDS_LIST,
  COMMENTS_MODIFIED_FIELD,
];

export const SELF_SERVICED_GROUND_MODIFIED_FIELDS_LIST: FieldItem[] = [
  ...DEFAULT_MODIFIED_FIELDS_LIST,
  COMMENTS_MODIFIED_FIELD,
];

export const TX_CENTER_GROUND_MODIFIED_FIELDS_LIST: FieldItem[] = [
  ...DEFAULT_MODIFIED_FIELDS_LIST,
  {
    fieldName: 'TX Center Provider',
    fields: ['provider'],
    converter: convertProviderChangesToValue,
  },
  ...NON_TMDX_GROUND_MODIFIED_FIELDS_LIST,
  COMMENTS_MODIFIED_FIELD,
];

export const DEVICE_DELIVERY_GROUND_MODIFIED_FIELDS_LIST: FieldItem[] = [
  ...DEFAULT_MODIFIED_FIELDS_LIST,
  {
    fieldName: 'Tracking link',
    fields: ['trackingNumber'],
    converter: convertPrimitiveToString,
  },
  COMMENTS_MODIFIED_FIELD,
];

export const GROUND_TRIP_REASSIGNED_FIELDS_LIST: FieldItem[] = [
  {
    fieldName: 'Ground trip re-assigned',
    fields: ['tripReference'],
    converter: convertReassignedTripReference,
  },
];

export const ACCOMMODATION_MODIFIED_FIELDS_LIST: FieldItem[] = [
  {
    fieldName: 'Hotel name',
    fields: ['hotelName'],
    converter: convertPrimitiveToString,
  },
  {
    fieldName: 'Hotel address',
    fields: ['location'],
    converter: convertHotelLocationChangedValues,
  },
  {
    fieldName: 'Resources',
    fields: ['resources'],
    converter: convertResourcesToValue,
  },
  {
    fieldName: 'Check-in date',
    fields: ['checkInAt'],
    converter: convertHotelDateTimeChangedValues('checkInAt'),
  },
  {
    fieldName: 'Check-out date',
    fields: ['checkOutAt'],
    converter: convertHotelDateTimeChangedValues('checkOutAt'),
  },
  {
    fieldName: 'Rooms',
    fields: ['rooms'],
    converter: convertPrimitiveToString,
  },
  {
    fieldName: 'Booking number',
    fields: ['bookingNumber'],
    converter: convertPrimitiveToString,
  },
  COMMENTS_MODIFIED_FIELD,
];
